'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import Navbar from '@/components/common/Navbar';

interface GalleryItem {
  id: string;
  title: string;
  description: string | null;
  media_type: string;
  image_url: string;
  is_featured: boolean;
}

export default function GalleryPage() {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const supabase = createClient();

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery_media')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setGalleryItems(data || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = activeFilter === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.media_type === activeFilter);

  const filters = [
    { value: 'all', label: 'All Photos' },
    { value: 'before_after', label: 'Before & After' },
    { value: 'team_photo', label: 'Our Team' },
    { value: 'service_photo', label: 'Services' }
  ];

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-green-50/30">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Photo Gallery</h1>
            <p className="text-xl text-white/90">See the quality of our work through before and after photos, team pictures, and service highlights.</p>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-20 section-gradient-mid dot-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeFilter === filter.value
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-green-50 border border-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Icon name="ArrowPathIcon" size={40} className="animate-spin text-green-600 mx-auto" />
              <p className="text-muted-foreground mt-4">Loading gallery...</p>
            </div>
          )}

          {/* Gallery Grid */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Icon name="PhotoIcon" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-muted-foreground">No photos found in this category.</p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <AppImage
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {item.is_featured && (
                        <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                          <Icon name="StarIcon" size={16} />
                          Featured
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                      {item.description && (
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                      )}
                      <div className="mt-4 flex items-center gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                          {item.media_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32] via-green-600 to-[#4CAF50] text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Space?</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Experience the same quality cleaning shown in our gallery. Book your service today!
          </p>
          <Link
            href="/homepage#booking"
            className="inline-flex items-center gap-2 bg-white text-green-700 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 transition-all shadow-lg hover:shadow-xl"
          >
            Book Now
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}